import {useRouter} from 'next/router';
import {useCallback, useState} from 'react';

import {UserEnrollmentsData} from 'src/types/page-props';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export interface UseAtRiskSurveyTriggerParams {
    showAtRiskFeedbackSurvey: boolean;
    userEnrollments?: UserEnrollmentsData;
}

export interface UseAtRiskSurveyTriggerResult {
    isEligibleForAtRiskSurvey: boolean;
    openAtRiskModal: (() => void) | undefined;
    isAtRiskModalOpen: boolean;
    closeAtRiskModal: () => void;
    lastCourseUrl: string | undefined;
    lastCourseId: string | undefined;
    handleSurveyLoaded: (result: {hasError: boolean}) => void;
}

export function useAtRiskSurveyTrigger({
    showAtRiskFeedbackSurvey,
    userEnrollments,
}: UseAtRiskSurveyTriggerParams): UseAtRiskSurveyTriggerResult {
    const [isAtRiskModalOpen, setIsAtRiskModalOpen] = useState(false);
    const [surveyGated, setSurveyGated] = useState(false);

    const {query} = useRouter();
    // Url override to support QA of feature
    const forceShowAtRisk = 'showAtRiskSurvey' in query;

    const courseEnrollments =
        userEnrollments?.me?.enrollments?.courseEnrollmentsPaged?.courseEnrollments ?? [];

    const lastCourseUrl = courseEnrollments[0]?.course?.urlCourseTaking ?? undefined;
    const lastCourseId = courseEnrollments[0]?.course?.id ?? undefined;

    // A user is offered the at-risk survey when every gate below passes. When
    // eligible, the JBI section shows the "How's your learning going?" trigger
    // link and the (closed) survey modal is mounted so it can prefetch.
    const isEligibleForAtRiskSurvey = (() => {
        if (!showAtRiskFeedbackSurvey) return false;

        if (forceShowAtRisk) return true;

        if (surveyGated) return false;

        // At-risk heuristic: 4+ days since the user last accessed any course.
        // Users with no course activity at all are excluded — they're handled
        // by other onboarding surfaces, not this survey.
        const timestamps = courseEnrollments
            .filter((e) => e?.lastAccessedTime)
            .map((e) => new Date(e?.lastAccessedTime as string).getTime());

        if (timestamps.length === 0) return false;

        const daysSinceLast = (Date.now() - Math.max(...timestamps)) / MS_PER_DAY;
        return daysSinceLast >= 4;
    })();

    // Memoized with no deps (setState setters are stable) so this reference stays
    // identical across renders — MyLearningUnitSection is wrapped in React.memo and
    // embeds a JBI video player; an unstable callback here would defeat that memo
    // and force it to re-render (and its player to re-evaluate) on every unrelated
    // change to this hook's own state (isAtRiskModalOpen, surveyGated).
    const openAtRiskModalCallback = useCallback(() => setIsAtRiskModalOpen(true), []);
    const openAtRiskModal = isEligibleForAtRiskSurvey ? openAtRiskModalCallback : undefined;

    const closeAtRiskModal = useCallback(() => setIsAtRiskModalOpen(false), []);

    const handleSurveyLoaded = useCallback(({hasError}: {hasError: boolean}) => {
        if (hasError) setSurveyGated(true);
    }, []);

    return {
        isEligibleForAtRiskSurvey,
        openAtRiskModal,
        isAtRiskModalOpen,
        closeAtRiskModal,
        lastCourseUrl,
        lastCourseId,
        handleSurveyLoaded,
    };
}
